import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Grid, Chip, Box, Pagination, Alert, LinearProgress, CardActionArea } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ordinal } from '../../utils/format';
import { apiService } from '../../services/apiService';

interface CongressItem {
  congress: number;
  bill_count: number;
  member_count: number;
}

// Compute approximate start/end dates for a given Congress number.
// Rule of thumb:
// - 1st Congress started Mar 4, 1789; each Congress spans two years.
// - After the 20th Amendment (effective 1935, 74th Congress), terms begin on Jan 3.
function getCongressDateRange(n: number): { start: string; end: string } {
  const startYear = 1789 + 2 * (n - 1);
  const useJan3 = n >= 74; // 74th Congress began Jan 3, 1935
  const startLabel = useJan3 ? `Jan 3, ${startYear}` : `Mar 4, ${startYear}`;
  const endYear = startYear + 2;
  const endLabel = useJan3 ? `Jan 3, ${endYear}` : `Mar 4, ${endYear}`;
  return { start: startLabel, end: endLabel };
}

const Congresses: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [items, setItems] = useState<CongressItem[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const resp = await apiService.getCongresses({ limit, offset });
        if (!cancelled) {
          setItems(resp.items || []);
          setTotal(resp.total || 0);
        }
      } catch (e) {
        if (!cancelled) {
          setError(true);
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [limit, offset]);

  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Congresses</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sessions listed here appear as the database fills up. Click a session to explore members and significant legislation.
      </Typography>
      {loading && <LinearProgress aria-label="Loading Congress sessions" />}
      {error && <Alert severity="error">Could not load Congress sessions. Please refresh to try again.</Alert>}
      {!loading && !error && items.length === 0 && <Typography>No Congress sessions available.</Typography>}
      <Grid container spacing={2}>
        {items.map((it) => {
          const range = getCongressDateRange(it.congress);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={it.congress}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea onClick={() => navigate(`/congress/${it.congress}`)} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5">{ordinal(it.congress)} Congress</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {range.start} – {range.end}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${it.bill_count} bills`} size="small" />
                    <Chip label={`${it.member_count} members`} size="small" />
                  </Box>
                </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination count={pageCount} page={page} onChange={(_, p) => setOffset((p - 1) * limit)} />
      </Box>
    </Box>
  );
};

export default Congresses;
